import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getDataForVehicleTrackingLoadCards,
  getLoadLineItemsForLoadHeader,
} from 'src/redux/Actions/VehicleTrackingOutward';
import { ReactComponent as BackIcon } from 'src/Images/icons/common/backIcon.svg';
import { ReactComponent as LoadingImage } from 'src/Images/icons/vehicle-tracking/loading-image.svg';
import { ReactComponent as TargetInfoIcon } from 'src/Images/icons/common/target-info-icon.svg';
import { ReactComponent as DocumentIcon } from 'src/Images/icons/planning/Document.svg';
import { ReactComponent as PackQuantityIcon } from 'src/Images/icons/planning/PackQuantity.svg';
import { ReactComponent as DeliveryQuantityIcon } from 'src/Images/icons/planning/DeliveryQuantity.svg';
import 'src/style/createVehicleTracking.css';

const DEFAULT_TABLE_FIELDS = [
  { label: 'Delivery Number', value: 'sap_code', key: 'sap_code' },
  { label: 'Delivery Date', value: 'delivery_date', key: 'delivery_date' },
  { label: 'Pack Type', value: 'pack_type', key: 'pack_type' },
  { label: 'Pack Quantity', value: 'pack_quantity', key: 'pack_quantity' },
  { label: 'Grade', value: 'grade', key: 'grade' },
  { label: 'Gross Weight', value: 'weight', key: 'weight' },
  {
    label: 'Product SAP Code',
    value: 'product_sap_code',
    key: 'product_sap_code',
  },
  {
    label: 'Product Description',
    value: 'product_description',
    key: 'product_description',
  },
  { label: 'Plant', value: 'plant', key: 'plant' },
  { label: 'Quantity', value: 'quantity', key: 'quantity' },
  { label: 'Batch', value: 'batch', key: 'batch' },
];

const getFirstValue = (...values) =>
  values.find((value) => value !== null && value !== undefined && value !== '');

const getNumericValue = (...values) => {
  const value = getFirstValue(...values);
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const formatDeliveryDate = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const day = date.getDate();
  const suffix =
    day % 10 === 1 && day !== 11
      ? 'st'
      : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
          ? 'rd'
          : 'th';
  const month = date.toLocaleString('en-US', { month: 'short' });

  return `${String(day).padStart(2, '0')}${suffix}, ${month} ${date.getFullYear()}`;
};

const formatNumber = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : value;
};

const formatWeight = (value, uom = 'KG') =>
  value !== null && value !== undefined && value !== ''
    ? `${formatNumber(value)} ${uom || ''}`.trim()
    : '--';

const calcVariance = (planned, actual) => {
  const plannedValue = Number(planned);
  const actualValue = Number(actual);
  return plannedValue > 0 && actual != null && Number.isFinite(actualValue)
    ? Number((((actualValue - plannedValue) / plannedValue) * 100).toFixed(1))
    : null;
};

const getDeliveryUniqueNumber = (item) => item.delivery_unique_number;

const getDeliveryColumnValue = (fieldKey, item) => {
  switch (fieldKey) {
    case 'delivery_date':
      return formatDeliveryDate(item.delivery_date);
    case 'product_sap_code':
      return item.product?.product_sap_code;
    case 'product_description':
      return item.product?.product_description;
    case 'plant':
      return item.plant?.name;
    default: {
      const value = item[fieldKey];
      return typeof value === 'object' && value !== null ? value.name : value;
    }
  }
};

const renderDeliveryColumnValue = (fieldKey, item) => {
  const value = getDeliveryColumnValue(fieldKey, item);

  return value !== null && value !== undefined && value !== '' ? value : '-';
};

function VariancePill({ value }) {
  if (value === null) return null;
  return (
    <span className={`text-md status-pill ${value >= 0 ? 'green' : 'red'}`}>
      {value > 0 ? '+' : ''}
      {value}% Variance
    </span>
  );
}

function DeliverySummaryCard({ Icon, label, value, uom, themeColor }) {
  const displayValue =
    typeof value === 'number' && uom !== 'Total' ? value.toFixed(2) : value;

  return (
    <div className="planning-dashboard-card div-flex-row div-space-between">
      <div className="div-flex-column rg-5 div-align-start">
        <span className="text-md text-semi-bold text-nowrap">{label}</span>
        <div className="div-flex-row cg-5 div-align-baseline text-nowrap">
          <span className="text-xxl text-bold">{displayValue ?? 0}</span>
          {uom && <span className="text-md text-grey">{uom}</span>}
        </div>
      </div>
      <div className="div-flex-column div-align-end">
        <span
          className={`delivery-summary-icon theme-text-color-${themeColor}`}
        >
          <Icon className="icon-size-xxxl" />
        </span>
      </div>
    </div>
  );
}

export default function VehicleTrackingCompletedLoadOverview({
  onBack,
  themeColor,
  vtLoadHeaderUniqueNumber,
  selectedLoad,
}) {
  const { vehicleTrackingNumber } = useParams();
  const dispatch = useDispatch();
  const authReducer = useSelector((state) => state.AuthReducer);
  const tenantOrganisationId = authReducer.userData.organisations?.id;

  const [loadDetails, setLoadDetails] = useState(null);
  const [loadLineItemsForHeader, setLoadLineItemsForHeader] = useState(null);
  const [activePackTypeSummaryKey, setActivePackTypeSummaryKey] =
    useState(null);

  useEffect(() => {
    if (
      !tenantOrganisationId ||
      !vehicleTrackingNumber ||
      !vtLoadHeaderUniqueNumber
    )
      return;

    dispatch(
      getDataForVehicleTrackingLoadCards(
        tenantOrganisationId,
        vehicleTrackingNumber,
        vtLoadHeaderUniqueNumber,
      ),
    ).then((data) => {
      setLoadDetails(data);
    });
  }, [
    dispatch,
    tenantOrganisationId,
    vehicleTrackingNumber,
    vtLoadHeaderUniqueNumber,
  ]);

  useEffect(() => {
    if (!tenantOrganisationId || !vtLoadHeaderUniqueNumber) return;

    dispatch(
      getLoadLineItemsForLoadHeader(
        tenantOrganisationId,
        vtLoadHeaderUniqueNumber,
      ),
    ).then((data) => {
      setLoadLineItemsForHeader(data);
    });
  }, [dispatch, tenantOrganisationId, vtLoadHeaderUniqueNumber]);

  const deliveryOrderList = useMemo(
    () =>
      loadLineItemsForHeader?.lineItems ||
      loadLineItemsForHeader?.line_items ||
      [],
    [loadLineItemsForHeader],
  );

  const fieldFilter = useMemo(() => {
    const responseFields =
      loadLineItemsForHeader?.fieldMapping ||
      loadLineItemsForHeader?.field_mapping ||
      [];

    return responseFields.length > 0 ? responseFields : DEFAULT_TABLE_FIELDS;
  }, [loadLineItemsForHeader]);

  const deliverySelectionSummary = useMemo(() => {
    const packTypeSummaryMap = {};

    deliveryOrderList.forEach((item) => {
      const packTypeKey = item.pack_type ?? item.packType ?? 'unassigned';
      const packTypeName = item.pack_type ?? item.packType ?? 'Unassigned';

      if (!packTypeSummaryMap[packTypeKey]) {
        packTypeSummaryMap[packTypeKey] = {
          summaryKey: `pack-type-${packTypeKey}`,
          packTypeName,
          documentUniqueNumbers: new Set(),
          totalPackQuantity: 0,
          totalDeliveryQuantity: 0,
          totalGrossWeight: 0,
          quantityUOM:
            item?.unit_of_measurement?.name ||
            item?.unitOfMeasurement?.name ||
            loadDetails?.loadingTotalQuantityUom,
        };
      }

      const deliveryUniqueNumber = getDeliveryUniqueNumber(item);
      if (deliveryUniqueNumber) {
        packTypeSummaryMap[packTypeKey].documentUniqueNumbers.add(
          deliveryUniqueNumber,
        );
      }

      packTypeSummaryMap[packTypeKey].totalPackQuantity += getNumericValue(
        item.pack_quantity,
        item.packQuantity,
      );
      packTypeSummaryMap[packTypeKey].totalDeliveryQuantity += getNumericValue(
        item.quantity,
        item.actual_quantity,
        item.actualQuantity,
        item.receiving_quantity,
        item.receivingQuantity,
      );
      packTypeSummaryMap[packTypeKey].totalGrossWeight += getNumericValue(
        item.weight,
        item.gross_weight,
        item.grossWeight,
      );
    });

    return Object.values(packTypeSummaryMap).map((summary) => ({
      ...summary,
      totalDocuments: summary.documentUniqueNumbers.size,
    }));
  }, [deliveryOrderList, loadDetails]);

  const activePackTypeSummary =
    deliverySelectionSummary.find(
      (summary) => summary.summaryKey === activePackTypeSummaryKey,
    ) || deliverySelectionSummary[0];

  const activeSummaryCards = [
    {
      label: 'Total Documents',
      key: 'total-documents',
      value: activePackTypeSummary?.totalDocuments,
      uom: 'Nos',
      Icon: DocumentIcon,
    },
    {
      label: 'Total Pack Quantity',
      key: 'total-pack-quantity',
      value: activePackTypeSummary?.totalPackQuantity,
      uom: 'Nos',
      Icon: PackQuantityIcon,
    },
    {
      label: 'Total Delivery Quantity',
      key: 'total-delivery-quantity',
      value: activePackTypeSummary?.totalDeliveryQuantity,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
    {
      label: 'Gross Weight',
      key: 'gross-weight',
      value: activePackTypeSummary?.totalGrossWeight,
      uom: activePackTypeSummary?.quantityUOM,
      Icon: DeliveryQuantityIcon,
    },
  ];

  const loadCode =
    loadDetails?.loadCode || selectedLoad?.vehicle_tracking_load_code || '--';
  const loadTareWeight = getFirstValue(
    loadDetails?.actualTareWeight,
    selectedLoad?.actual_tare_weight,
  );
  const tareWeightUom = getFirstValue(
    loadDetails?.actualTareWeightUom,
    selectedLoad?.actual_tare_weight_uom,
    'KG',
  );
  const plannedLoadGrossWeight = getFirstValue(
    loadDetails?.plannedLoadGrossWeight,
    selectedLoad?.planned_load_gross_weight,
  );
  const actualGrossWeight = getFirstValue(
    loadDetails?.actualGrossWeight,
    selectedLoad?.actual_gross_weight,
  );
  const actualLoadGrossWeight =
    getFirstValue(
      loadDetails?.actualLoadGrossWeight,
      selectedLoad?.actual_load_gross_weight,
    ) ??
    (loadTareWeight != null && actualGrossWeight != null
      ? Number((Number(actualGrossWeight) - Number(loadTareWeight)).toFixed(2))
      : null);
  const loadWeightUom = getFirstValue(
    loadDetails?.actualLoadGrossWeightUom,
    selectedLoad?.actual_load_gross_weight_uom,
    loadDetails?.actualGrossWeightUom,
    selectedLoad?.actual_gross_weight_uom,
    'KG',
  );
  const plannedGrossWeight = getFirstValue(
    loadDetails?.plannedGrossWeight,
    selectedLoad?.planned_gross_weight,
  );
  const grossWeightUom = getFirstValue(
    loadDetails?.actualGrossWeightUom,
    selectedLoad?.actual_gross_weight_uom,
    'KG',
  );
  const loadVariance = calcVariance(
    plannedLoadGrossWeight,
    actualLoadGrossWeight,
  );
  const grossWeightVariance = calcVariance(
    plannedGrossWeight,
    actualGrossWeight,
  );

  return (
    <div className="div-flex-column rg-10 h-100 padding-top-10">
      <div className="div-flex-row div-space-between div-align-center">
        <div className="div-flex-row cg-20 div-align-center">
          <div role="button" tabIndex={0} onClick={onBack} aria-label="Go back">
            <BackIcon className="icon-size-xxxl" />
          </div>
          <div className="div-flex-row cg-5 div-align-center">
            <LoadingImage className="icon-size-xxl" />
            <span className="text-lg text-bold">Load Overview</span>
          </div>
          <span className="light-blue-pill text-md">{loadCode}</span>
          <span className="text-sm status-pill green">Completed</span>
        </div>
      </div>

      <div
        className={`div-flex-column rg-20 theme-scrollable-container-${themeColor}`}
      >
        <Row className="m-0 g-3 vertical-padding-10">
          <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
            <div className="manage-load-card">
              <span className="manage-load-card-badge text-semi-bold">
                Weight Bridge
              </span>
              <div className="div-flex-column rg-5">
                <span className="text-md text-semi-bold">Tare Weight</span>
                <div className="div-flex-row cg-5 div-align-center">
                  <TargetInfoIcon className="icon-size-sm" />
                  <span className="text-md text-grey text-bold">
                    Weight before loading
                  </span>
                </div>
              </div>
              <span className={`text-md status-pill-${themeColor}`}>
                {formatWeight(loadTareWeight, tareWeightUom)}
              </span>
            </div>
          </Col>

          <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
            <div className="manage-load-card">
              <span className="manage-load-card-badge text-semi-bold">
                Confirm Loading
              </span>
              <div className="div-flex-column rg-5">
                <span className="text-md text-semi-bold">Load Weight</span>
                <div className="div-flex-row cg-5 div-align-center">
                  <TargetInfoIcon className="icon-size-sm" />
                  <span className="text-md text-grey text-bold">
                    Planned {plannedLoadGrossWeight ?? '--'}
                  </span>
                </div>
              </div>
              <div className="div-flex-row cg-5 div-align-center">
                <span className="text-md status-pill red">
                  {formatWeight(actualLoadGrossWeight, loadWeightUom)}
                </span>
                <VariancePill value={loadVariance} />
              </div>
            </div>
          </Col>

          <Col xxl={4} xl={4} lg={4} md={4} sm={12} xs={12} className="m-0">
            <div className="manage-load-card">
              <span className="manage-load-card-badge text-semi-bold">
                Weight Bridge
              </span>
              <div className="div-flex-column rg-5">
                <span className="text-md text-semi-bold">Gross Weight</span>
                <div className="div-flex-row cg-5 div-align-center">
                  <TargetInfoIcon className="icon-size-sm" />
                  <span className="text-md text-grey text-bold">
                    Planned {plannedGrossWeight ?? '--'}
                  </span>
                </div>
              </div>
              <div className="div-flex-row cg-5 div-align-center">
                <span className="text-md status-pill red">
                  {formatWeight(actualGrossWeight, grossWeightUom)}
                </span>
                <VariancePill value={grossWeightVariance} />
              </div>
            </div>
          </Col>
        </Row>

        <div className="div-flex-column rg-10">
          {deliverySelectionSummary.length > 0 && (
            <>
              <div className="div-flex-row cg-5">
                {deliverySelectionSummary.map((packTypeSummary) => {
                  const isActive =
                    packTypeSummary.summaryKey ===
                    activePackTypeSummary?.summaryKey;

                  return (
                    <button
                      key={packTypeSummary.summaryKey}
                      type="button"
                      className={`div-flex-row div-align-center text-sm ${
                        isActive
                          ? `theme-pill-active-${themeColor}`
                          : 'border border-radius-4 vertical-padding-5 horizontal-padding-10 text-grey'
                      }`}
                      onClick={() =>
                        setActivePackTypeSummaryKey(packTypeSummary.summaryKey)
                      }
                    >
                      <span>{packTypeSummary.packTypeName}</span>
                    </button>
                  );
                })}
              </div>
              <div className="div-flex-row cg-5 delivery-selection-summary-row">
                {activeSummaryCards.map((summaryCard) => (
                  <div
                    key={summaryCard.key}
                    className="delivery-selection-summary-item"
                  >
                    <DeliverySummaryCard
                      Icon={summaryCard.Icon}
                      label={summaryCard.label}
                      value={summaryCard.value}
                      uom={summaryCard.uom}
                      themeColor={themeColor}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="purchase-order-line-item-card border-radius-4">
          <div className="div-space-between div-align-center px-2 py-2 border-bottom">
            <span className="text-md text-bold delivery-header">
              Selected Delivery
            </span>
          </div>
          <div className="po-table-wrapper">
            <table>
              <thead className="outward-delivery-table-head">
                <tr>
                  {fieldFilter.map((field) => (
                    <th key={field.value}>{field.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {deliveryOrderList.length > 0 ? (
                  deliveryOrderList.map((item) => (
                    <tr key={item.id}>
                      {fieldFilter.map((field) => (
                        <td key={field.value}>
                          {renderDeliveryColumnValue(field.key, item)}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={fieldFilter.length}
                      className="text-center text-grey"
                    >
                      No selected delivery available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
